import React, { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { 
  Upload, 
  TestTube, 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  ExternalLink,
  Trash2
} from 'lucide-react'
import { googleMerchantService } from '../../services/googleMerchantService'
import { productService } from '../../services/api'

const GoogleMerchantUpdater = () => {
  const [selectedProduct, setSelectedProduct] = useState('')
  const [manualProductData, setManualProductData] = useState({
    productId: '',
    title: '',
    description: '',
    price: '',
    currency: 'KWD',
    link: '',
    imageLink: '',
    brand: '',
    availability: 'in stock',
    condition: 'new',
    gtin: '',
    mpn: '',
    googleProductCategory: '',
    productType: ''
  })

  // Fetch products from your database
  const { data: productsResponse } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getProducts({ limit: 100 }),
  })

  const products = productsResponse?.products || []

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: googleMerchantService.testConnection,
    onSuccess: (data) => {
      toast.success('✅ اتصال Google Merchant Center ناجح!')
      console.log('Connection test result:', data)
    },
    onError: (error) => {
      toast.error('❌ فشل في الاتصال بـ Google Merchant Center')
      console.error('Connection test failed:', error)
    }
  })

  // Update product mutation
  const updateProductMutation = useMutation({
    mutationFn: googleMerchantService.updateProduct,
    onSuccess: (data) => {
      toast.success('✅ تم تحديث المنتج في Google Merchant Center بنجاح!')
      console.log('Product updated:', data)
    },
    onError: (error) => {
      toast.error('❌ فشل في تحديث المنتج في Google Merchant Center')
      console.error('Product update failed:', error)
    }
  })

  // Bulk update products mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: googleMerchantService.bulkUpdateProducts,
    onSuccess: (data) => {
      const { successful = [], failed = [], total = 0 } = data.data || {}
      toast.success(`✅ تم تحديث ${successful.length} من أصل ${total} منتج بنجاح!`)
      if (failed.length > 0) {
        toast.error(`⚠️ فشل في تحديث ${failed.length} منتج`)
      }
      console.log('Bulk update completed:', data)
    },
    onError: (error) => {
      toast.error('❌ فشل في التحديث الجماعي للمنتجات')
      console.error('Bulk update failed:', error)
    }
  })

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: googleMerchantService.deleteProduct,
    onSuccess: (data) => {
      toast.success('✅ تم حذف المنتج من Google Merchant Center بنجاح!')
      console.log('Product deleted:', data)
    },
    onError: (error) => {
      toast.error('❌ فشل في حذف المنتج من Google Merchant Center')
      console.error('Product delete failed:', error)
    }
  })

  const handleProductSelect = (productId) => {
    setSelectedProduct(productId)
    const product = products.find(p => p.id === parseInt(productId))
    
    if (product) {
      setManualProductData({
        productId: product.id.toString(),
        title: product.name || '',
        description: product.description || '',
        price: product.price || '',
        currency: 'KWD',
        link: `${window.location.origin}/products/${product.id}`,
        imageLink: product.featured_image ? `${window.location.origin}/uploads/products/${product.featured_image}` : '',
        brand: product.brand || 'سبحات',
        availability: product.stock_quantity > 0 ? 'in stock' : 'out of stock',
        condition: 'new',
        gtin: product.gtin || '',
        mpn: product.sku || '',
        googleProductCategory: product.google_category || '',
        productType: product.category_name || ''
      })
    }
  }

  const handleInputChange = (field, value) => {
    setManualProductData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleUpdateProduct = () => {
    if (!manualProductData.productId || !manualProductData.title || !manualProductData.price) {
      toast.error('يرجى ملء الحقول المطلوبة: معرف المنتج، العنوان، والسعر')
      return
    }

    updateProductMutation.mutate(manualProductData)
  }

  const handleDeleteProduct = () => {
    if (!manualProductData.productId) {
      toast.error('يرجى تحديد معرف المنتج للحذف')
      return
    }

    if (confirm('هل أنت متأكد من حذف هذا المنتج من Google Merchant Center؟')) {
      deleteProductMutation.mutate(manualProductData.productId)
    }
  }

  const handleBulkUpdate = () => {
    if (confirm(`هل أنت متأكد من تحديث جميع المنتجات (${products.length}) في Google Merchant Center؟ قد تستغرق هذه العملية بعض الوقت.`)) {
      bulkUpdateMutation.mutate()
    }
  }

  const handleTestConnection = () => {
    testConnectionMutation.mutate()
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            إدارة Google Merchant Center
          </h2>
          <button
            onClick={handleTestConnection}
            disabled={testConnectionMutation.isLoading}
            className="btn btn-outline flex items-center"
          >
            {testConnectionMutation.isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <TestTube className="h-4 w-4 mr-2" />
            )}
            اختبار الاتصال
          </button>
        </div>

        {/* Product Selection */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">اختيار المنتج</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                اختر منتج من قاعدة البيانات
              </label>
              <select
                value={selectedProduct}
                onChange={(e) => handleProductSelect(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">-- اختر منتجاً --</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.name} (ID: {product.id})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                أو أدخل معرف المنتج يدوياً
              </label>
              <input
                type="text"
                value={manualProductData.productId}
                onChange={(e) => handleInputChange('productId', e.target.value)}
                placeholder="معرف المنتج"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Product Data Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Required Fields */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">الحقول المطلوبة</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                عنوان المنتج *
              </label>
              <input
                type="text"
                value={manualProductData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                السعر *
              </label>
              <div className="flex">
                <input
                  type="number"
                  step="0.01"
                  value={manualProductData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  className="flex-1 p-3 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <select
                  value={manualProductData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="p-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="KWD">KWD</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                رابط المنتج *
              </label>
              <input
                type="url"
                value={manualProductData.link}
                onChange={(e) => handleInputChange('link', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                رابط صورة المنتج *
              </label>
              <input
                type="url"
                value={manualProductData.imageLink}
                onChange={(e) => handleInputChange('imageLink', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          {/* Optional Fields */}
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">الحقول الاختيارية</h4>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الوصف
              </label>
              <textarea
                value={manualProductData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows="3"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                الماركة
              </label>
              <input
                type="text"
                value={manualProductData.brand}
                onChange={(e) => handleInputChange('brand', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                التوفر
              </label>
              <select
                value={manualProductData.availability}
                onChange={(e) => handleInputChange('availability', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="in stock">متوفر</option>
                <option value="out of stock">غير متوفر</option>
                <option value="preorder">طلب مسبق</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GTIN
              </label>
              <input
                type="text"
                value={manualProductData.gtin}
                onChange={(e) => handleInputChange('gtin', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                فئة Google
              </label>
              <input
                type="text"
                value={manualProductData.googleProductCategory}
                onChange={(e) => handleInputChange('googleProductCategory', e.target.value)}
                placeholder="مثال: Religious & Ceremonial > Religious Items"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                نوع المنتج (سيتم حفظه كـ Custom Label)
              </label>
              <input
                type="text"
                value={manualProductData.productType}
                onChange={(e) => handleInputChange('productType', e.target.value)}
                placeholder="مثال: منتجات دينية"
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                ملاحظة: سيتم حفظ نوع المنتج كـ Custom Label في Google Merchant Center
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4">
          <button
            onClick={handleBulkUpdate}
            disabled={bulkUpdateMutation.isLoading || products.length === 0}
            className="btn btn-success flex items-center"
          >
            {bulkUpdateMutation.isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            تحديث جميع المنتجات ({products.length})
          </button>

          <button
            onClick={handleUpdateProduct}
            disabled={updateProductMutation.isLoading}
            className="btn btn-primary flex items-center"
          >
            {updateProductMutation.isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 mr-2" />
            )}
            تحديث منتج واحد
          </button>

          <button
            onClick={handleDeleteProduct}
            disabled={deleteProductMutation.isLoading}
            className="btn btn-danger flex items-center"
          >
            {deleteProductMutation.isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            حذف من Google Merchant Center
          </button>

          <a
            href="https://merchants.google.com"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline flex items-center"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            فتح Google Merchant Center
          </a>
        </div>

        {/* Status Messages */}
        {bulkUpdateMutation.isLoading && (
          <div className="mt-6 p-4 rounded-lg border bg-blue-50 border-blue-200">
            <div className="flex items-center text-blue-700">
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              <span>جاري تحديث المنتجات في Google Merchant Center... قد تستغرق العملية بضع دقائق</span>
            </div>
          </div>
        )}

        {(testConnectionMutation.isSuccess || testConnectionMutation.isError) && (
          <div className="mt-6 p-4 rounded-lg border">
            {testConnectionMutation.isSuccess ? (
              <div className="flex items-center text-green-700 bg-green-50 border-green-200">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span>تم الاتصال بـ Google Merchant Center بنجاح!</span>
              </div>
            ) : (
              <div className="flex items-center text-red-700 bg-red-50 border-red-200">
                <AlertCircle className="h-5 w-5 mr-2" />
                <span>فشل الاتصال بـ Google Merchant Center</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default GoogleMerchantUpdater
